/**
 * Ventas por Producto — reporte profesional con 2 vistas (tabs):
 *  - Resumen por Producto (ranking, cantidad, facturas, subtotal, IVA, total, % del total)
 *  - Detalle por Cliente (qué cliente compró cada producto)
 *
 * Filtros: período (YYYY-MM) o rango personalizado.
 * Exports: Excel + PDF.
 */

import { useState, useEffect } from 'react';
import {
  BarChart3, Calendar, Download, FileSpreadsheet, FileText,
  RefreshCw, Package, Users as UsersIcon, DollarSign, Receipt, TrendingUp, Loader2
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { reportesApi } from '../../utils/api';
import MonthRangePicker from '../../components/MonthRangePicker';

const fmtCRC = (v) => '₡' + (parseFloat(v) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (v) => (parseFloat(v) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function VentasProducto() {
  const [periodo, setPeriodo] = useState(dayjs().format('YYYY-MM'));
  // Wrapper para MonthRangePicker (modo-mes only — reporte por mes específico)
  const filtroFecha = (() => {
    const [y, m] = periodo.split('-').map(Number);
    const last = new Date(y, m, 0).getDate();
    return { modo: 'mes', periodo, desde: `${periodo}-01`, hasta: `${periodo}-${String(last).padStart(2, '0')}` };
  })();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('producto'); // 'producto' | 'cliente'
  const [productoExpandido, setProductoExpandido] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const res = await reportesApi.getVentasProducto({ periodo });
      if (res.data?.success) setData(res.data.data);
      else toast.error('Error al cargar el reporte');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al cargar');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [periodo]);

  const descargarExcel = async () => {
    setExportingExcel(true);
    try {
      const res = await reportesApi.exportVentasProducto({ periodo });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `Ventas_por_Producto_${periodo}.xlsx`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) { toast.error('Error al descargar Excel'); }
    finally { setExportingExcel(false); }
  };

  const descargarPdf = async () => {
    setExportingPdf(true);
    try {
      const res = await reportesApi.exportVentasProductoPdf({ periodo });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `Ventas_por_Producto_${periodo}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('PDF descargado');
    } catch (e) { toast.error('Error al descargar PDF'); }
    finally { setExportingPdf(false); }
  };

  const productosFiltrados = (data?.productos || []).filter(p => {
    if (!busqueda) return true;
    const q = busqueda.toLowerCase();
    return (p.detalle || '').toLowerCase().includes(q) ||
           (p.codigo || '').toLowerCase().includes(q) ||
           (p.codigoComercial || '').toLowerCase().includes(q);
  });

  return (
    <div className="w-full flex flex-col h-[var(--alto-contenido)]">
      {/* Header + filtros */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm shrink-0 mb-4">
        <div className="px-5 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-purple-100">
              <BarChart3 className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Ventas por Producto</h2>
              <p className="text-sm text-gray-500">Análisis de productos vendidos con detalle por cliente</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={descargarPdf} disabled={exportingPdf || loading}
              className="px-4 py-2 border-2 border-red-400 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {exportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              PDF
            </button>
            <button onClick={descargarExcel} disabled={exportingExcel || loading}
              className="px-4 py-2 border-2 border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
              {exportingExcel ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Excel
            </button>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-3 flex-wrap">
          <MonthRangePicker
            value={filtroFecha}
            onChange={(v) => setPeriodo(v.periodo)}
            onRefresh={cargar}
            onlyMonth
          />
          <div className="flex-1" />
          <input type="text" placeholder="Buscar producto…" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-purple-200" />
        </div>
      </div>

      {/* KPI cards */}
      {data && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 shrink-0 mb-4">
          <KPICard color="purple" Icon={Package} label="Productos" value={data.resumen.totalProductos} sub="únicos" />
          <KPICard color="blue" Icon={TrendingUp} label="Cantidad vendida" value={fmtNum(data.resumen.totalCantidad)} />
          <KPICard color="cyan" Icon={Receipt} label="Subtotal" value={fmtCRC(data.resumen.totalSubtotalCRC)} sub="sin IVA" />
          <KPICard color="amber" Icon={DollarSign} label="IVA" value={fmtCRC(data.resumen.totalIVACRC)} sub="cobrado" />
          <KPICard color="emerald" Icon={DollarSign} label="Total ventas" value={fmtCRC(data.resumen.totalVentasCRC)} sub={`${data.resumen.totalFacturas} facturas`} />
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="border-b border-gray-200 shrink-0">
          <nav className="flex gap-1 px-4">
            <TabButton active={tab === 'producto'} onClick={() => setTab('producto')} Icon={Package}>Resumen por Producto</TabButton>
            <TabButton active={tab === 'cliente'} onClick={() => setTab('cliente')} Icon={UsersIcon}>Detalle por Cliente</TabButton>
          </nav>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="h-8 w-8 text-purple-500 animate-spin" /></div>
        ) : !data || productosFiltrados.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <Package className="h-12 w-12 mb-3 text-gray-300" />
            <p className="text-sm">Sin datos para el período seleccionado</p>
          </div>
        ) : tab === 'producto' ? (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-purple-50 shadow-[0_1px_0_rgba(229,231,235,1)]">
                <tr className="border-b border-purple-200">
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">#</th>
                  <th className="text-left px-3 py-3 font-semibold text-purple-700">Código</th>
                  <th className="text-left px-3 py-3 font-semibold text-purple-700">Producto / Servicio</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">Cantidad</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">Facturas</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">Subtotal CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">IVA CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">Total CRC</th>
                  <th className="text-right px-3 py-3 font-semibold text-purple-700">% Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {productosFiltrados.map(p => (
                  <tr key={`${p.codigo}-${p.detalle}`} className="hover:bg-purple-50/40">
                    <td className="px-3 py-2 text-right font-mono text-xs text-gray-500">{p.ranking}</td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700">{p.codigo || '-'}</td>
                    <td className="px-3 py-2 text-gray-900">{p.detalle}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtNum(p.cantidad)}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{p.facturas}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-700">{fmtCRC(p.subtotalCRC)}</td>
                    <td className="px-3 py-2 text-right font-mono text-gray-600">{fmtCRC(p.ivaCRC)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-purple-700">{fmtCRC(p.totalCRC)}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-gray-500">{p.porcentaje.toFixed(2)}%</span>
                        <div className="w-16 h-1.5 bg-gray-100 rounded overflow-hidden">
                          <div className="h-full bg-purple-500" style={{ width: `${Math.min(p.porcentaje, 100)}%` }} />
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0 z-10 bg-purple-100 border-t-2 border-purple-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                <tr className="font-bold text-purple-900">
                  <td colSpan={3} className="px-3 py-2.5 text-right">TOTAL</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmtNum(data.resumen.totalCantidad)}</td>
                  <td className="px-3 py-2.5 text-right">{data.resumen.totalFacturas}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmtCRC(data.resumen.totalSubtotalCRC)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmtCRC(data.resumen.totalIVACRC)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmtCRC(data.resumen.totalVentasCRC)}</td>
                  <td className="px-3 py-2.5 text-right">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          // Tab: por cliente
          <div className="flex-1 min-h-0 overflow-auto">
            <div className="divide-y divide-gray-100">
              {productosFiltrados.map((p, idx) => {
                const detalleCliente = data.porCliente.find(pc => pc.codigo === p.codigo && pc.detalle === p.detalle);
                const clientes = detalleCliente?.clientes || [];
                const expandido = productoExpandido === `${p.codigo}-${idx}`;
                return (
                  <div key={`${p.codigo}-${idx}`}>
                    <button onClick={() => setProductoExpandido(expandido ? null : `${p.codigo}-${idx}`)}
                      className="w-full px-4 py-3 flex items-center gap-3 hover:bg-purple-50/40 text-left">
                      <span className="text-xs font-mono text-gray-400">#{p.ranking}</span>
                      <span className="text-xs font-mono text-gray-500 w-24 truncate">{p.codigo || '-'}</span>
                      <span className="flex-1 text-gray-900 font-medium truncate">{p.detalle}</span>
                      <span className="text-xs text-gray-500">{clientes.length} cliente{clientes.length !== 1 ? 's' : ''}</span>
                      <span className="font-mono font-semibold text-purple-700">{fmtCRC(p.totalCRC)}</span>
                      <span className="text-gray-400">{expandido ? '▲' : '▼'}</span>
                    </button>
                    {expandido && (
                      <div className="bg-purple-50/30 px-12 py-2">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-purple-700 border-b border-purple-200">
                              <th className="text-left py-2 px-2 font-semibold">Cliente</th>
                              <th className="text-left py-2 px-2 font-semibold">Identif.</th>
                              <th className="text-right py-2 px-2 font-semibold">Cantidad</th>
                              <th className="text-right py-2 px-2 font-semibold">Facturas</th>
                              <th className="text-right py-2 px-2 font-semibold">Total CRC</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientes.map((c, ci) => (
                              <tr key={ci} className="border-b border-purple-100 last:border-0">
                                <td className="py-1.5 px-2 text-gray-800">{c.clienteNombre}</td>
                                <td className="py-1.5 px-2 text-gray-500 font-mono">{c.clienteIdentificacion}</td>
                                <td className="py-1.5 px-2 text-right font-mono">{fmtNum(c.cantidad)}</td>
                                <td className="py-1.5 px-2 text-right">{c.facturas}</td>
                                <td className="py-1.5 px-2 text-right font-mono font-semibold text-purple-700">{fmtCRC(c.totalCRC)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ color, Icon, label, value, sub }) {
  const colors = {
    purple: 'from-purple-50 to-purple-100 border-purple-200 text-purple-700',
    blue: 'from-blue-50 to-blue-100 border-blue-200 text-blue-700',
    cyan: 'from-cyan-50 to-cyan-100 border-cyan-200 text-cyan-700',
    amber: 'from-amber-50 to-amber-100 border-amber-200 text-amber-700',
    emerald: 'from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-700'
  };
  const iconBg = { purple: 'bg-purple-600', blue: 'bg-blue-600', cyan: 'bg-cyan-600', amber: 'bg-amber-600', emerald: 'bg-emerald-600' };
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className={`px-3 py-2 bg-gradient-to-r ${colors[color]} border-b flex items-center gap-2`}>
        <div className={`p-1 ${iconBg[color]} rounded`}><Icon className="h-3.5 w-3.5 text-white" /></div>
        <h3 className="font-semibold text-xs text-gray-800">{label}</h3>
      </div>
      <div className="px-3 py-2.5">
        <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, Icon, children }) {
  return (
    <button onClick={onClick}
      className={`py-2.5 px-4 border-b-2 text-sm font-medium transition-colors flex items-center gap-2 ${
        active ? 'border-purple-500 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
      }`}>
      <Icon className="h-4 w-4" /> {children}
    </button>
  );
}
